// Pure, dependency-free CSV builder for the Niagara station point catalog.
// Spec: docs/plans/2026-08-13-001-feat-point-mapping-csv-export-plan.md
// ("Phase 1 — Pure CSV module + tests" section).
//
// Ports export_niagara_station.py so the same catalog can be produced from the
// browser, using data the Point Mapping page already holds in memory. Both
// implementations must stay byte-compatible: same 11 columns, same UTF-8 BOM,
// same CRLF terminators, same ISO-8601 timestamp form.
//
// Note this WRITES real RFC-4180 quoting, unlike utilityMeterCsv.js which
// rejects quoted input outright. A reader can be strict; a writer cannot —
// Niagara statuses such as "{disabled,stale} @ def" contain commas, and 55 rows
// in production corrupt without quoting.

export const NIAGARA_CSV_COLUMNS = [
    'StationName',
    'HostId',
    'Handle',
    'SlotPath',
    'PointValue',
    'PointName',
    'PointType',
    'Status',
    'Units',
    'Mapped',
    'StationLastUpdatedAt',
];

// Excel only honours UTF-8 in a CSV when the file opens with a BOM. Without it
// the 1,293 degree-sign units in production render as "Â°F". Written as an
// escape rather than a literal so it stays visible in source and survives
// editors that strip zero-width characters.
const BOM = '\uFEFF';

// RFC 4180 says CRLF; Python's csv module writes CRLF by default, so matching
// it keeps the two implementations diffable.
const ROW_SEPARATOR = '\r\n';

// Niagara escapes characters that aren't legal in a slot name as $xx (two hex
// digits) or $uxxxx (four). A leading digit gets escaped too, which is why
// "$32nd_Floor" appears in the wild for "2nd_Floor".
const ESCAPE_LONG = /\$u([0-9a-fA-F]{4})/g;
const ESCAPE_SHORT = /\$([0-9a-fA-F]{2})/g;

const NEEDS_QUOTING = /[",\r\n]/;

// Decodes Niagara $xx / $uxxxx escapes into readable characters.
//
// The result is for humans, not for Niagara: a decoded path is no longer a
// valid ORD and may not resolve if pasted back in, particularly where a leading
// digit was escaped. Safe to apply everywhere in the UI because mapping submits
// on hostId + handle, never on slotPath.
//
// A lone "$" not followed by valid hex is left untouched.
export const decodeSlotPath = path => {
    if (!path) {
        return path;
    }
    return String(path)
        .replace(ESCAPE_LONG, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(ESCAPE_SHORT, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
};

// Wraps a field in quotes and doubles interior quotes when it contains a
// quote, comma, CR or LF. Null and undefined become an empty field.
const escapeCsvField = value => {
    const text = value === null || value === undefined ? '' : String(value);
    if (NEEDS_QUOTING.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

// Niagara sends the *string* "null" about as often as it omits the units field
// (2,903 vs 2,812 points in production). Both mean "no unit", so both render
// as an empty cell.
const cleanUnits = units => {
    if (units === null || units === undefined || units === 'null') {
        return '';
    }
    return units;
};

// An ISO date-time carrying no offset. `new Date()` parses these as LOCAL time,
// whereas export_niagara_station.py explicitly assumes UTC. Today the API always
// emits a trailing Z (Npgsql maps the timestamptz column with Kind=Utc), so the
// two agree by luck rather than by construction. Appending Z makes them agree on
// purpose — otherwise a driver or column-type change would silently shift every
// browser export by the viewer's UTC offset, and verifier invariant 10 checks
// shape only, so a shifted timestamp would still pass.
const OFFSETLESS_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?$/;

// ISO 8601, UTC, truncated to seconds — 2026-08-12T16:42:21Z. Matches
// export_niagara_station.py's strftime('%Y-%m-%dT%H:%M:%SZ') exactly so the two
// outputs diff cleanly. An absent or unparseable value yields an empty cell
// rather than "Invalid Date".
const formatStationTimestamp = value => {
    if (!value) {
        return '';
    }
    const text = String(value);
    const parsed = new Date(OFFSETLESS_TIMESTAMP.test(text) ? `${text}Z` : text);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }
    return parsed.toISOString().replace(/\.\d+Z$/, 'Z');
};

// Mapped points are keyed on the pair (stationHostId, handle) — the same pair
// mapPoint submits and handlePointMappingSelect looks up. A Set keeps the join
// O(1) per row; the largest station is 3,805 points against ~3,100 mappings,
// which a .find() per row would turn into millions of comparisons.
//
// equipmentService.getMappedEquipmentPoints returns {error, message} on an HTTP
// error and null on a network failure, and that value lands straight in state —
// so anything that isn't an array is treated as "no mappings known".
const buildMappedKeySet = mappedEquipmentPoints => {
    const keys = new Set();
    if (!Array.isArray(mappedEquipmentPoints)) {
        return keys;
    }
    mappedEquipmentPoints.forEach(mappedPoint => {
        // A null handle would interpolate to the string "null" and collide with any
        // other null-handled row on the same station. GetMappedEquipmentPoints has no
        // `AND handle IS NOT NULL` filter, unlike the Python side, so skip them here.
        if (!mappedPoint || mappedPoint.handle === null || mappedPoint.handle === undefined) {
            return;
        }
        keys.add(`${mappedPoint.stationHostId}\u0000${mappedPoint.handle}`);
    });
    return keys;
};

// Builds the CSV text for a flattened point list.
//
// Returns the complete file contents: a UTF-8 BOM, a header row, then one row
// per point in the order given (which is JSON document order, as the JACE sent
// it). Every row is CRLF-terminated, including the last.
//
// - points come from PointMapping's flatten and carry stationName, hostId,
//   handle, slotPath, pointValue, pointName, pointType, status, units and
//   stationLastUpdatedAt.
// - slotPath must already be decoded by the caller. This function deliberately
//   does NOT decode again: decodeSlotPath is not idempotent, because $24 is
//   Niagara's escape for a literal $, so "$2432nd" decodes to "$32nd" and then
//   to "2nd". Decoding twice would also break parity with the Python exporter,
//   which decodes exactly once.
// - a leaf with no pointData yields blank PointValue and Status, and the row is
//   still emitted so the catalog stays complete.
export const buildNiagaraStationCsv = (points, mappedEquipmentPoints) => {
    const mappedKeys = buildMappedKeySet(mappedEquipmentPoints);
    const rows = Array.isArray(points) ? points : [];

    const lines = [NIAGARA_CSV_COLUMNS.join(',')];

    rows.forEach(point => {
        const mapped =
            point.handle !== null && point.handle !== undefined &&
            mappedKeys.has(`${point.hostId}\u0000${point.handle}`);
        lines.push([
            escapeCsvField(point.stationName),
            escapeCsvField(point.hostId),
            escapeCsvField(point.handle),
            escapeCsvField(point.slotPath),
            escapeCsvField(point.pointValue),
            escapeCsvField(point.pointName),
            escapeCsvField(point.pointType),
            escapeCsvField(point.status),
            escapeCsvField(cleanUnits(point.units)),
            escapeCsvField(mapped),
            escapeCsvField(formatStationTimestamp(point.stationLastUpdatedAt)),
        ].join(','));
    });

    return `${BOM}${lines.join(ROW_SEPARATOR)}${ROW_SEPARATOR}`;
};

// Anything outside this set is replaced, so the name is safe on every platform.
const UNSAFE_FILENAME_CHARS = /[^A-Za-z0-9._-]+/g;

const sanitizeForFilename = name =>
    String(name === null || name === undefined ? '' : name)
        .replace(UNSAFE_FILENAME_CHARS, '_')
        .replace(/^_+|_+$/g, '');

// Two-digit zero pad for the filename stamp.
const pad = value => String(value).padStart(2, '0');

// Builds the download filename, e.g.
//   Pathian_Parkview_niagara_station_data_2026-08-13_143205.csv
//
// Named for the station when the group has exactly one (all seven production
// groups do today), falling back to the group id when it has several or none.
//
// The stamp is LOCAL time to second precision, matching the timestamp the page
// already displays. Seconds matter because the contents are volatile: Mapped
// flips as a user works through a mapping pass, and PointValue/Status move
// whenever the JACE posts. Colons are omitted — illegal in Windows filenames.
//
// `now` is passed in rather than read from the clock so this stays testable.
export const buildNiagaraCsvFilename = (points, groupId, now) => {
    const rows = Array.isArray(points) ? points : [];
    const stationNames = [...new Set(rows.map(point => point.stationName).filter(Boolean))];

    let label = stationNames.length === 1 ? sanitizeForFilename(stationNames[0]) : '';
    if (!label) {
        label = sanitizeForFilename(`group_${groupId}`) || 'niagara';
    }

    const stamp =
        `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
        `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    return `${label}_niagara_station_data_${stamp}.csv`;
};
